/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * Module Registry
 *
 * Central registry of all business modules available in the CoreX platform.
 * Each module definition describes its identity, icon, color, logo paths,
 * and base route. Used by the module shell navigation components.
 */

import {
  Shield,
  Package,
  Users,
  Calculator,
  FolderKanban,
  type LucideIcon,
} from 'lucide-react';

// Logo imports — dark
import corexLogoDark from '@/assets/logos/dark/corex-logo-dark.svg';
import ventaLogoDark from '@/assets/logos/dark/ventacorex-logo-dark.svg';
import hrLogoDark from '@/assets/logos/dark/hrcorex-logo-dark.svg';
import comptaLogoDark from '@/assets/logos/dark/comptacorex-logo-dark.svg';
import pmLogoDark from '@/assets/logos/dark/pmcorex-logo-dark.svg';

// Logo imports — light
import corexLogoLight from '@/assets/logos/light/corex-logo-light.svg';
import ventaLogoLight from '@/assets/logos/light/ventacorex-logo-light.svg';
import hrLogoLight from '@/assets/logos/light/hrcorex-logo-light.svg';
import comptaLogoLight from '@/assets/logos/light/comptacorex-logo-light.svg';
import pmLogoLight from '@/assets/logos/light/pmcorex-logo-light.svg';

export type LayoutStyle = 'rail' | 'topbar' | 'dropdown';

export interface ModuleDefinition {
  id: string;
  nameKey: string;
  subtitleKey: string;
  shortName: string;
  icon: LucideIcon;
  color: string;
  basePath: string;
  logos: { dark: string; light: string };
  order: number;
  isAdmin?: boolean;
}

export const MODULE_REGISTRY: ModuleDefinition[] = [
  {
    id: 'admin',
    nameKey: 'moduleSwitcher.admin.name',
    subtitleKey: 'moduleSwitcher.admin.subtitle',
    shortName: 'ADM',
    icon: Shield,
    color: '#8B0000',
    basePath: '/superadmin',
    logos: { dark: corexLogoDark, light: corexLogoLight },
    order: 0,
    isAdmin: true,
  },
  {
    id: 'venta',
    nameKey: 'moduleSwitcher.venta.name',
    subtitleKey: 'moduleSwitcher.venta.subtitle',
    shortName: 'VTA',
    icon: Package,
    color: '#dc2626',
    basePath: '/venta',
    logos: { dark: ventaLogoDark, light: ventaLogoLight },
    order: 1,
  },
  {
    id: 'hr',
    nameKey: 'moduleSwitcher.hr.name',
    subtitleKey: 'moduleSwitcher.hr.subtitle',
    shortName: 'HR',
    icon: Users,
    color: '#2563eb',
    basePath: '/hr',
    logos: { dark: hrLogoDark, light: hrLogoLight },
    order: 2,
  },
  {
    id: 'compta',
    nameKey: 'moduleSwitcher.compta.name',
    subtitleKey: 'moduleSwitcher.compta.subtitle',
    shortName: 'CMP',
    icon: Calculator,
    color: '#16a34a',
    basePath: '/compta',
    logos: { dark: comptaLogoDark, light: comptaLogoLight },
    order: 3,
  },
  {
    id: 'pm',
    nameKey: 'moduleSwitcher.pm.name',
    subtitleKey: 'moduleSwitcher.pm.subtitle',
    shortName: 'PM',
    icon: FolderKanban,
    color: '#9333ea',
    basePath: '/pm',
    logos: { dark: pmLogoDark, light: pmLogoLight },
    order: 4,
  },
];

export function getModuleById(id: string): ModuleDefinition | undefined {
  return MODULE_REGISTRY.find((m) => m.id === id);
}

export function getModuleByPath(pathname: string): ModuleDefinition | undefined {
  return MODULE_REGISTRY.find((m) => pathname.startsWith(m.basePath));
}
